/**
 * API client for the website.
 *
 * Content requests go to /api/headless/v1/* using:
 *   x-tenant-slug (from hostname or env)
 *   x-space-slug  (from hostname or env)
 *
 * Auth requests go to /customer/auth/* using customer JWT.
 */

import { getSiteIdentifiers } from './site';

// ─── Headless fetch (tenant + space from hostname) ────────────────────────────

async function headlessFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { tenantSlug, spaceSlug } = getSiteIdentifiers();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
    'x-tenant-slug': tenantSlug,
    'x-space-slug': spaceSlug,
  };

  return fetch(`/api/headless/v1${path}`, { ...init, headers });
}

export async function headlessGet<T>(path: string): Promise<T> {
  const res = await headlessFetch(path, { method: 'GET' });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}

export async function headlessPost<T>(path: string, body: unknown): Promise<T> {
  const res = await headlessFetch(path, { method: 'POST', body: JSON.stringify(body) });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number; data?: unknown };
    (err as Error & { status: number; data?: unknown }).status = res.status;
    try {
      (err as Error & { status: number; data?: unknown }).data = await res.json();
    } catch { /* ignore */ }
    throw err;
  }
  return res.json() as Promise<T>;
}

// ─── Auth fetch (customer JWT) ────────────────────────────────────────────────

import { getToken, getRefreshToken, setTokenPair, clearAuth, type TokenPair } from './auth';

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const rt = getRefreshToken();
      if (!rt) return null;
      const { tenantSlug, spaceSlug } = getSiteIdentifiers();
      const res = await fetch('/customer/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-slug': tenantSlug,
          'x-space-slug': spaceSlug,
        },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) { clearAuth(); return null; }
      const data = (await res.json()) as TokenPair;
      setTokenPair(data);
      return data.accessToken;
    } catch {
      clearAuth();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

interface AuthFetchOptions extends RequestInit {
  skipAuth?: boolean;
}

async function authFetch(url: string, options: AuthFetchOptions = {}): Promise<Response> {
  const { skipAuth, ...init } = options;
  const { tenantSlug, spaceSlug } = getSiteIdentifiers();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
    'x-tenant-slug': tenantSlug,
    'x-space-slug': spaceSlug,
  };

  const token = skipAuth ? null : getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['x-authorization'] = `Bearer ${token}`; // CloudFront doesn't forward Authorization
  }

  let res = await fetch(url, { ...init, headers });

  if (res.status === 401 && !skipAuth) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      headers['x-authorization'] = `Bearer ${newToken}`;
      res = await fetch(url, { ...init, headers });
    } else {
      // Don't redirect to login on public pages (preview, edit mode)
      const isPublicPage = window.location.pathname.startsWith('/preview/') ||
        new URLSearchParams(window.location.search).get('editMode') === 'true';
      if (!isPublicPage) {
        window.location.href = '/login';
      }
    }
  }

  return res;
}

export async function apiPost<T>(url: string, body: unknown, options?: AuthFetchOptions): Promise<T> {
  const res = await authFetch(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number; data?: unknown };
    err.status = res.status;
    try { err.data = await res.json(); } catch { /* ignore */ }
    throw err;
  }
  return res.json() as Promise<T>;
}

export async function apiGet<T>(url: string, options?: AuthFetchOptions): Promise<T> {
  const res = await authFetch(url, { ...options, method: 'GET' });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number };
    (err as Error & { status: number }).status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}
